import { useEffect, useState } from 'react'
import { listMarkets, type MarketView, type MarketsReport } from '../../lib/adminClient'
import { LoadError } from '../../components/LoadError'
import { Card, Pill } from './ui'

// The target markets, and how outreach to each is going.
//
// This is the way in to the day's work: pick a market, see what is waiting in
// it, open it. Everything on this tab is a count of real rows, computed from
// the same reachPlan the person view uses, so a market can never claim work
// that the people in it do not show.
//
// Nothing is rated, scored or projected. A market nobody has written to has no
// reply rate at all — it says "nothing sent yet", because 0% would read as a
// market that failed rather than one that has not been tried.

const STEP_TONE: Record<string, 'accent' | 'warn' | 'good' | 'muted'> = {
  reply: 'good',
  message: 'accent',
  email: 'accent',
  form: 'accent',
  follow_up: 'warn',
  verify: 'warn',
  connect: 'muted',
  start_there: 'muted',
  no_route: 'muted'
}

function Figure({ n, label, tone }: { n: number; label: string; tone?: string }): JSX.Element {
  return (
    <div>
      <div className={`text-xl font-semibold tabular-nums ${tone ?? 'text-slate-100'}`}>{n}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  )
}

function Market({ m, onOpen }: { m: MarketView; onOpen: (key: string) => void }): JSX.Element {
  const nothingSent = m.contacted === 0
  return (
    <Card
      testId="market-row"
      title={m.key}
      aside={
        <button
          className="btn-primary text-xs disabled:opacity-40"
          disabled={m.readyNow === 0}
          onClick={() => onOpen(m.key)}
          data-testid={`market-open-${m.key}`}
        >
          {m.readyNow > 0 ? `Work these ${m.readyNow}` : 'Nothing waiting'}
        </button>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
        <Figure n={m.people} label="people" />
        <Figure n={m.readyNow} label="ready now" tone={m.readyNow ? 'text-accent' : 'text-slate-500'} />
        <Figure n={m.contacted} label="contacted" />
        <Figure n={m.replied} label="replied" tone={m.replied ? 'text-emerald-300' : 'text-slate-500'} />
        <Figure n={m.signedUp} label="signed up" tone={m.signedUp ? 'text-emerald-300' : 'text-slate-500'} />
        <div>
          <div className="text-xl font-semibold tabular-nums text-slate-100">
            {nothingSent ? <span className="text-slate-500 text-sm font-normal">—</span> : `${m.replyRate}%`}
          </div>
          <div className="text-[11px] text-slate-500">{nothingSent ? 'nothing sent yet' : 'reply rate'}</div>
        </div>
      </div>

      {m.work.length > 0 ? (
        <div className="mb-3">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-1.5">What is waiting</div>
          <div className="flex flex-wrap gap-1.5">
            {m.work.map((w) => (
              <Pill key={w.step} tone={STEP_TONE[w.step] ?? 'muted'} testId={`market-work-${w.step}`}>
                {w.count} × {w.title}
              </Pill>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-500 mb-3">
          Nothing to do in this market right now.
          {m.contacted > 0 ? ' Everything sent is waiting on a reply.' : ''}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-slate-500">
        <span>
          Reachable by:{' '}
          <span className="text-slate-300">{m.reach.email} email</span>,{' '}
          <span className="text-slate-300">{m.reach.form} contact form</span>,{' '}
          <span className="text-slate-300">{m.reach.linkedin} LinkedIn only</span>
          {m.reach.none > 0 && (
            <>
              , <span className="text-amber-200">{m.reach.none} no route at all</span>
            </>
          )}
        </span>
        {m.held > 0 && <span className="text-red-300">{m.held} held back</span>}
        {m.unchecked > 0 && <span className="text-amber-200">{m.unchecked} facts not checked</span>}
        {m.personas.length > 0 && (
          <span>
            Persona: <span className="text-slate-300">{m.personas.map((p) => `${p.name} (${p.people})`).join(', ')}</span>
          </span>
        )}
        {m.personas.length === 0 && <span className="text-slate-600">No persona written for this market</span>}
        {m.lastSentAt && <span>Last sent {m.lastSentAt.slice(0, 10)}</span>}
      </div>
    </Card>
  )
}

export function Markets({ onOpenMarket }: { onOpenMarket: (key: string) => void }): JSX.Element {
  const [report, setReport] = useState<MarketsReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void listMarkets().then((r) => (r.ok ? setReport(r.data) : setError(r.error)))
  }, [])

  if (error) return <LoadError what="markets" endpoint="GET /admin/prospects/markets" error={error} testId="markets-error" />
  if (!report) return <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading…</div>

  const { markets, totals, noMarket } = report

  if (markets.length === 0) {
    return (
      <div className="glass-card rounded-xl p-6 text-sm text-slate-300" data-testid="markets-empty">
        No prospects stored yet, so there are no markets to work.
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="markets">
      <Card title="Across every market">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Figure n={markets.length} label="markets" />
          <Figure n={totals.people} label="people" />
          <Figure n={totals.readyNow} label="ready now" tone={totals.readyNow ? 'text-accent' : 'text-slate-500'} />
          <Figure n={totals.replied} label="replied" tone={totals.replied ? 'text-emerald-300' : 'text-slate-500'} />
        </div>
        {totals.contacted === 0 && (
          <div className="text-xs text-slate-500 mt-3" data-testid="markets-nothing-sent">
            Nothing has been sent to anyone yet, so no market has a reply rate. The first number worth watching is whether a
            market replies at all.
          </div>
        )}
        {noMarket > 0 && (
          <div className="text-xs text-amber-200 mt-3" data-testid="markets-no-market">
            {noMarket} {noMarket === 1 ? 'person has' : 'people have'} no market on their record. They are shown below under
            &ldquo;No market recorded&rdquo; rather than left out of the totals.
          </div>
        )}
      </Card>

      {markets.map((m) => (
        <Market key={m.key} m={m} onOpen={onOpenMarket} />
      ))}
    </div>
  )
}
